/**
 * Mavjud Go yadro endpointlarini X1 `Fetcher` kontraktiga moslash.
 *
 * NIMA UCHUN ADAPTER: jonli `/api/v1/admin/*` endpointlari bir xil shaklga ega
 * emas — `{users:[…]}`, `{countries:[…]}`, `{items:[…]}`, `{stats:{…}}`, va
 * sanalar epoch-ms RAQAM (ISO satr emas). Hech biri X1 talab qiladigan
 * (filtr + saralash + offset + total) kontraktni bermaydi. Adapter shu farqni
 * BITTA joyda ushlab turadi: server tomoni to'g'rilangach faqat shu fayl
 * o'zgaradi, 614 ekran emas.
 *
 * Har bir shakl JONLI javob bilan tekshirilgan (taxmin qilinmagan) —
 * dastlab `stats` va `countries` boshqacha deb o'ylangan edi va xato chiqdi.
 *
 * ⚠️ CHEKLOVLAR (tz/admin/HOLAT.md §«A dan kerak»):
 *   · `/admin/users` — `offset` va `total` YO'Q → 2-sahifaga o'tib bo'lmaydi
 *   · saralash server tomonda qo'llab-quvvatlanmaydi → mahalliy saralanadi
 *   · `/admin/countries` to'liq ro'yxatni qaytaradi → filtr/sahifa klientda
 */

import { api, ApiError } from './api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

/* ── Turlar (jonli yadro javoblari) ───────────────────────────── */

export interface AdminStats {
  users: number
  dau: number
  messages_today: number
  dialogs: number
  media_files: number
}

export interface AdminUserRow {
  id: number
  first_name?: string | null
  last_name?: string | null
  username?: string | null
  phone?: string | null
  status: string
  /** Epoch ms (jonli yadro RAQAM qaytaradi, ISO satr emas). */
  created_at?: string | number | null
  last_seen?: string | number | null
}

export interface CountryRow {
  code: string
  name: string
  nationality?: string | null
  dial_code: string
  flag?: string | null
  users: number
  is_blocked: boolean
}

export interface AuditRow {
  id?: number
  /** Epoch ms. */
  created_at: string | number
  admin?: string | null
  action: string
  target?: string | null
  /** Sabab/kontekst — ban sababi, four-eyes belgisi va h.k. (server qaytaradi). */
  detail?: string | null
  ip?: string | null
}

/* ── Mahalliy yordamchilar ────────────────────────────────────── */

/** Qator maydonini kalit bo'yicha o'qish — tip cheklovisiz. */
function field(row: unknown, key: string): unknown {
  return (row as Record<string, unknown>)[key]
}

function matchesFilters<T>(row: T, params: FetchParams): boolean {
  return params.filters.every((f) => {
    const v = field(row, f.key)
    switch (f.op) {
      case 'contains':
        return String(v ?? '').toLowerCase().includes(String(f.value).toLowerCase())
      case 'equals':
        return String(v ?? '') === String(f.value)
      case 'startsWith':
        return String(v ?? '').toLowerCase().startsWith(String(f.value).toLowerCase())
      case 'is':
        return Boolean(v) === Boolean(f.value)
      case 'in':
        return Array.isArray(f.value) && f.value.map(String).includes(String(v))
      case 'between': {
        if (!Array.isArray(f.value)) return true
        const [a, b] = f.value
        const n = typeof v === 'number' ? v : Number(v)
        if (a !== '' && a !== undefined && n < Number(a)) return false
        if (b !== '' && b !== undefined && n > Number(b)) return false
        return true
      }
      default:
        return true
    }
  })
}

function sortLocally<T>(rows: T[], params: FetchParams): T[] {
  const spec = params.sort[0]
  if (!spec) return rows
  const dir = spec.desc ? -1 : 1
  return [...rows].sort((x, y) => {
    const a = field(x, spec.key)
    const b = field(y, spec.key)
    if (a === b) return 0
    if (a === null || a === undefined) return 1
    if (b === null || b === undefined) return -1
    if (typeof a === 'number' && typeof b === 'number') return (a - b) * dir
    return String(a).localeCompare(String(b)) * dir
  })
}

function paginate<T>(rows: T[], params: FetchParams): FetchResult<T> {
  const start = (params.page - 1) * params.pageSize
  return { rows: rows.slice(start, start + params.pageSize), total: rows.length }
}

/* ── A3.01 Foydalanuvchilar ───────────────────────────────────── */

export async function fetchUsers(params: FetchParams): Promise<FetchResult<AdminUserRow>> {
  const data = await api.get<{ users: AdminUserRow[] }>('/admin/users', {
    query: { limit: params.pageSize, q: params.search || undefined },
    signal: params.signal,
  })
  // Server saralash/filtrni bilmaydi — qolganini klientda bajaramiz.
  // Bu FAQAT bitta sahifa doirasida to'g'ri; server tomoni kelgach olib tashlanadi.
  const rows = sortLocally(
    (data.users ?? []).filter((r) => matchesFilters(r, params)),
    params,
  )
  return { rows }
}

/** A5.01 — bot akkaunt (GET /admin/bots). */
export interface AdminBotRow {
  id: number
  username?: string | null
  first_name?: string | null
  is_official: boolean
  created_at: number
  last_seen?: number | null
}
export async function fetchBots(params: FetchParams): Promise<FetchResult<AdminBotRow>> {
  const data = await api.get<{ bots: AdminBotRow[] }>('/admin/bots', {
    query: { limit: params.pageSize, q: params.search || undefined },
    signal: params.signal,
  })
  const rows = sortLocally(
    (data.bots ?? []).filter((r) => matchesFilters(r, params)),
    params,
  )
  return { rows }
}

/** A10.01 — wallet tranzaksiyasi (GET /admin/transactions). */
export interface AdminTransaction {
  id: number
  user_id: number
  username?: string | null
  delta: number
  kind?: string | null
  peer_id?: number | null
  note?: string | null
  created_at: number
}
export async function fetchTransactions(params: FetchParams): Promise<FetchResult<AdminTransaction>> {
  const data = await api.get<{ transactions: AdminTransaction[] }>('/admin/transactions', {
    query: { limit: params.pageSize },
    signal: params.signal,
  })
  const rows = (data.transactions ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

export interface AdminTransactionDetail extends AdminTransaction {
  idempotency_key?: string | null
}
export async function fetchTransaction(id: number, signal?: AbortSignal): Promise<AdminTransactionDetail | null> {
  const data = await api.get<{ transaction: AdminTransactionDetail }>(
    `/admin/transactions/${id}`,
    signal ? { signal } : {},
  )
  return data.transaction ?? null
}

/** A4.01 — guruh/kanal ro'yxati (GET /admin/groups). */
export interface AdminGroupRow {
  id: number
  type: string
  title?: string | null
  username?: string | null
  member_count: number
  is_forum: boolean
  owner_id?: number | null
  created_at: number
}
export async function fetchGroups(params: FetchParams): Promise<FetchResult<AdminGroupRow>> {
  const data = await api.get<{ groups: AdminGroupRow[] }>('/admin/groups', {
    query: { limit: params.pageSize, q: params.search || undefined },
    signal: params.signal,
  })
  const rows = sortLocally(
    (data.groups ?? []).filter((r) => matchesFilters(r, params)),
    params,
  )
  return { rows }
}

/** A4.08 — taklif havolasi (GET /admin/groups/:id/invites). Token QAYTMAYDI. */
export interface AdminInvite {
  id: number
  name?: string | null
  usage_count: number
  usage_limit?: number | null
  expire_date?: number | null
  is_primary: boolean
  is_revoked: boolean
  creator?: string | null
  created_at: number
}
export async function fetchGroupInvites(id: number, params: FetchParams): Promise<FetchResult<AdminInvite>> {
  const data = await api.get<{ invites: AdminInvite[] }>(`/admin/groups/${id}/invites`, {
    signal: params.signal,
  })
  const rows = (data.invites ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/** A14.01/02 — feature-flag (GET /admin/settings/flags). Faqat feature_* (secret sizmaydi). */
export interface AdminFeatureFlag {
  key: string
  value: string
  enabled: boolean
  updated_at: number
}
export async function fetchFeatureFlags(params: FetchParams): Promise<FetchResult<AdminFeatureFlag>> {
  const data = await api.get<{ flags: AdminFeatureFlag[] }>('/admin/settings/flags', {
    signal: params.signal,
  })
  const rows = (data.flags ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
export async function fetchFeatureFlag(key: string, signal?: AbortSignal): Promise<AdminFeatureFlag | null> {
  const data = await api.get<{ flags: AdminFeatureFlag[] }>('/admin/settings/flags', signal ? { signal } : {})
  return (data.flags ?? []).find((f) => f.key === key) ?? null
}

/** A14.03/04/06 — flag yozuv (POST /admin/settings/flags, `feature_` only, audited).
 *  `enabled` → true/false; `value` → xom qiymat (rollout foizi va h.k.). */
export async function setFeatureFlag(key: string, opts: { enabled?: boolean; value?: string }): Promise<void> {
  await api.post('/admin/settings/flags', { key, ...opts })
}

/** A14.13/16 — bitta flagning o'zgarish tarixi (audit-asosli). `value` = o'sha
 *  paytda qo'llangan qiymat → rollback shu qiymatni setFeatureFlag bilan qaytaradi. */
export interface FlagHistoryRow {
  id: number
  admin?: string | null
  value: string | null
  ip?: string | null
  created_at: string | number
}
export async function fetchFlagHistory(key: string, signal?: AbortSignal): Promise<FlagHistoryRow[]> {
  const data = await api.get<{ items: FlagHistoryRow[] }>('/admin/settings/flags/history', {
    query: { key },
    ...(signal ? { signal } : {}),
  })
  return data.items ?? []
}

/** A14.05 — flag auditoriya (segment). Bo'sh ikkala ro'yxat = cheklovsiz. */
export interface FlagSegment {
  countries: string[]
  platforms: string[]
}
export async function fetchFlagSegment(key: string, signal?: AbortSignal): Promise<FlagSegment> {
  const data = await api.get<{ segment: FlagSegment }>('/admin/settings/flags/segment', {
    query: { key },
    ...(signal ? { signal } : {}),
  })
  return { countries: data.segment?.countries ?? [], platforms: data.segment?.platforms ?? [] }
}
export async function setFlagSegment(key: string, seg: FlagSegment): Promise<void> {
  await api.post('/admin/settings/flags/segment', { key, ...seg })
}

/** A14.07 — flag oldindan-shart (dependency) flaglari (to'liq kalitlar). Bo'sh = shartsiz. */
export async function fetchFlagDeps(key: string, signal?: AbortSignal): Promise<string[]> {
  const data = await api.get<{ deps: string[] }>('/admin/settings/flags/deps', {
    query: { key },
    ...(signal ? { signal } : {}),
  })
  return data.deps ?? []
}
export async function setFlagDeps(key: string, deps: string[]): Promise<void> {
  await api.post('/admin/settings/flags/deps', { key, deps })
}

/** A14.14 — flag qiymat-sxemasi (type + cheklovlar) + joriy qiymat mosligi. */
export interface FlagSchemaDef {
  type: string // '' | bool | int | percent | enum | string
  min?: number | null
  max?: number | null
  values?: string[]
}
export interface FlagSchemaInfo {
  schema: FlagSchemaDef
  value: string
  valid: boolean
  reason: string
}
export async function fetchFlagSchema(key: string, signal?: AbortSignal): Promise<FlagSchemaInfo> {
  const d = await api.get<FlagSchemaInfo>('/admin/settings/flags/schema', {
    query: { key },
    ...(signal ? { signal } : {}),
  })
  return { schema: d.schema ?? { type: '' }, value: d.value ?? '', valid: d.valid ?? true, reason: d.reason ?? '' }
}
export async function setFlagSchema(key: string, def: FlagSchemaDef): Promise<void> {
  await api.post('/admin/settings/flags/schema', { key, ...def })
}

/** A14.09/10 — A/B tajribalar (variant + og'irlik, per-user barqaror tayinlash). */
export interface ExperimentVariant {
  key: string
  weight: number
}
export interface Experiment {
  name: string
  enabled: boolean
  variants: ExperimentVariant[]
}
export async function fetchExperiments(signal?: AbortSignal): Promise<Experiment[]> {
  const d = await api.get<{ items: Experiment[] }>('/admin/settings/experiments', signal ? { signal } : {})
  return d.items ?? []
}
export async function saveExperiment(exp: Experiment): Promise<void> {
  await api.post('/admin/settings/experiments', exp)
}
export async function deleteExperiment(name: string): Promise<void> {
  await api.del('/admin/settings/experiments', { query: { name } })
}
export async function previewExperiment(name: string, n = 5000, signal?: AbortSignal): Promise<{ sample: number; distribution: Record<string, number> }> {
  const d = await api.get<{ sample: number; distribution: Record<string, number> }>('/admin/settings/experiments/preview', {
    query: { name, n },
    ...(signal ? { signal } : {}),
  })
  return { sample: d.sample ?? n, distribution: d.distribution ?? {} }
}

/** A15 — admin broadcast/e'lon (GET/POST /admin/broadcasts). Backend `admin_broadcasts`. */
export interface AdminBroadcast {
  id: number
  title: string
  body: string
  audience: string // all | active | staff
  status: string // draft | queued | sent | failed | cancelled
  recipients: number
  created_at: number
  sent_at: number | null
  scheduled_at: number | null
  test_sent_at: number | null
  cancelled_at: number | null
}
export async function fetchBroadcasts(params: FetchParams): Promise<FetchResult<AdminBroadcast>> {
  const data = await api.get<{ broadcasts: AdminBroadcast[] }>('/admin/broadcasts', {
    query: { limit: 300 },
    signal: params.signal,
  })
  const rows = (data.broadcasts ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
/** A15.12 — faqat rejalashtirilgan e'lonlar (scheduled_at IS NOT NULL). */
export async function fetchScheduledBroadcasts(params: FetchParams): Promise<FetchResult<AdminBroadcast>> {
  const data = await api.get<{ broadcasts: AdminBroadcast[] }>('/admin/broadcasts', {
    query: { limit: 300, scheduled: 1 },
    signal: params.signal,
  })
  const rows = (data.broadcasts ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
export async function fetchBroadcast(id: number, signal?: AbortSignal): Promise<AdminBroadcast | null> {
  const data = await api.get<{ broadcast: AdminBroadcast }>(`/admin/broadcasts/${id}`, signal ? { signal } : {})
  return data.broadcast ?? null
}
export function createBroadcast(body: {
  title?: string
  body: string
  audience?: string
  scheduled_at?: number
}): Promise<{ id: number; status: string }> {
  return api.post<{ id: number; status: string }>('/admin/broadcasts', body)
}
/** A15.09 test · A15.11 cancel · queue — POST /admin/broadcasts/:id/action. */
export function broadcastAction(id: number, action: 'queue' | 'cancel' | 'test'): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/broadcasts/${id}/action`, { action })
}

/** A15.17 — push-provayder holati (GET /admin/push/health). */
export interface AdminPushHealth {
  fcm_configured: boolean
  push_tokens: number
  by_provider: Record<string, number>
  broadcast_status: Record<string, number>
}
export async function fetchPushHealth(signal?: AbortSignal): Promise<AdminPushHealth> {
  const data = await api.get<{ health: AdminPushHealth }>('/admin/push/health', signal ? { signal } : {})
  return data.health
}

/** A15.21 — broadcast shablonlari (GET/POST/DELETE /admin/broadcast-templates). */
export interface AdminBroadcastTemplate {
  id: number
  name: string
  body: string
  audience: string
  created_at: number
}
export async function fetchBroadcastTemplates(params: FetchParams): Promise<FetchResult<AdminBroadcastTemplate>> {
  const data = await api.get<{ templates: AdminBroadcastTemplate[] }>('/admin/broadcast-templates', {
    signal: params.signal,
  })
  const rows = (data.templates ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
export function createBroadcastTemplate(body: { name?: string; body: string; audience?: string }): Promise<{ id: number }> {
  return api.post<{ id: number }>('/admin/broadcast-templates', body)
}
export function deleteBroadcastTemplate(id: number): Promise<{ deleted: boolean }> {
  return api.del<{ deleted: boolean }>(`/admin/broadcast-templates/${id}`)
}

/** A15.10 throttle + A15.22 anti-spam — GET/PUT /admin/broadcast-settings. */
export interface AdminBroadcastSettings {
  throttle_per_min: number
  daily_cap: number
  cooldown_hours: number
  antispam_enabled: boolean
}
export async function fetchBroadcastSettings(signal?: AbortSignal): Promise<AdminBroadcastSettings> {
  const data = await api.get<{ settings: AdminBroadcastSettings }>('/admin/broadcast-settings', signal ? { signal } : {})
  return data.settings
}
export function updateBroadcastSettings(body: AdminBroadcastSettings): Promise<{ ok: boolean }> {
  return api.put<{ ok: boolean }>('/admin/broadcast-settings', body)
}

/** A15.20 — xizmat/maintenance banner (GET/PUT /admin/service-banner). */
export interface AdminServiceBanner {
  text: string
  severity: string // info | warning | critical
  enabled: boolean
}
export async function fetchServiceBanner(signal?: AbortSignal): Promise<AdminServiceBanner> {
  const data = await api.get<{ banner: AdminServiceBanner }>('/admin/service-banner', signal ? { signal } : {})
  return data.banner
}
export function updateServiceBanner(body: AdminServiceBanner): Promise<{ ok: boolean }> {
  return api.put<{ ok: boolean }>('/admin/service-banner', body)
}

/** A7.06 — support tiket (GET /admin/tickets). Backend `650928d`. */
export interface AdminTicket {
  id: number
  user_id: number
  username?: string | null
  subject: string
  category: string
  status: string
  priority: string
  assigned_to?: number | null
  created_at: number
  updated_at: number
}
export interface AdminTicketReply {
  author_id: number
  is_staff: boolean
  body: string
  created_at: number
}
export interface AdminTicketDetail extends AdminTicket {
  replies: AdminTicketReply[]
}
export async function fetchTickets(params: FetchParams): Promise<FetchResult<AdminTicket>> {
  const data = await api.get<{ tickets: AdminTicket[] }>('/admin/tickets', {
    query: { limit: 300 },
    signal: params.signal,
  })
  const rows = (data.tickets ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
export async function fetchTicket(id: number, signal?: AbortSignal): Promise<AdminTicketDetail | null> {
  const data = await api.get<{ ticket: AdminTicketDetail }>(`/admin/tickets/${id}`, signal ? { signal } : {})
  return data.ticket ?? null
}
export function replyTicket(id: number, body: string): Promise<unknown> {
  return api.post(`/admin/tickets/${id}/reply`, { body })
}
export function setTicketStatus(id: number, status: string): Promise<unknown> {
  return api.post(`/admin/tickets/${id}/status`, { status })
}
/** SLA chegarasi: ochiq/kutilayotgan tiket shu vaqtdan oshsa "kechikkan" (A7.13/14). */
export const TICKET_SLA_MS = 72 * 3600 * 1000
/** A7.13 — SLA statistikasi uchun xom ro'yxat (≤300). */
export async function fetchAllTickets(signal?: AbortSignal): Promise<AdminTicket[]> {
  const data = await api.get<{ tickets: AdminTicket[] }>('/admin/tickets', {
    query: { limit: 300 },
    ...(signal ? { signal } : {}),
  })
  return data.tickets ?? []
}
/** A7.14 — kechikkan tiketlar: ochiq/kutilayotgan + yosh > SLA. `now` klientdan. */
export async function fetchOverdueTickets(params: FetchParams): Promise<FetchResult<AdminTicket>> {
  const data = await api.get<{ tickets: AdminTicket[] }>('/admin/tickets', {
    query: { limit: 300 },
    signal: params.signal,
  })
  const now = Date.now()
  const rows = (data.tickets ?? [])
    .filter((x) => (x.status === 'open' || x.status === 'pending') && now - x.created_at > TICKET_SLA_MS)
    .filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/** A2.03 — moderatsiya-dashboard: barcha trust&safety navbatlarining KUTAYOTGAN soni.
 *  Jonli endpointlar ustidan (status=pending filtri) — yangi backend kerak emas. */
export interface ModerationOverview {
  reports_pending: number
  tickets_open: number
  appeals_pending: number
  verifications_pending: number
  legal_pending: number
}
export async function fetchModerationOverview(signal?: AbortSignal): Promise<ModerationOverview> {
  const opt = (status: string) => ({ query: { status, limit: 300 }, ...(signal ? { signal } : {}) })
  const [reports, tickets, appeals, verif, legal] = await Promise.all([
    api.get<{ reports: AdminReport[] }>('/admin/reports', opt('pending')),
    api.get<{ tickets: AdminTicket[] }>('/admin/tickets', opt('open')),
    api.get<{ appeals: AdminAppeal[] }>('/admin/appeals', opt('pending')),
    api.get<{ requests: AdminVerification[] }>('/admin/verification', opt('pending')),
    api.get<{ requests: AdminLegalRequest[] }>('/admin/legal', opt('pending')),
  ])
  return {
    reports_pending: (reports.reports ?? []).length,
    tickets_open: (tickets.tickets ?? []).length,
    appeals_pending: (appeals.appeals ?? []).length,
    verifications_pending: (verif.requests ?? []).length,
    legal_pending: (legal.requests ?? []).length,
  }
}

/** A2.07 — support-dashboard: tiketlarni holat/prioritet/kategoriya bo'yicha
 *  yig'adi (jonli `/admin/tickets` oynasi ustidan, agregatsiya-endpoint kutmaydi). */
export interface SupportOverview {
  total: number
  by_status: Record<string, number>
  by_priority: Record<string, number>
  by_category: { key: string; count: number }[]
  recent: AdminTicket[]
}
export async function fetchSupportOverview(signal?: AbortSignal): Promise<SupportOverview> {
  const data = await api.get<{ tickets: AdminTicket[] }>('/admin/tickets', {
    query: { limit: 300 },
    ...(signal ? { signal } : {}),
  })
  const rows = data.tickets ?? []
  const tally = (pick: (t: AdminTicket) => string) => {
    const m: Record<string, number> = {}
    for (const t of rows) m[pick(t)] = (m[pick(t)] ?? 0) + 1
    return m
  }
  const cat = tally((t) => t.category || '—')
  return {
    total: rows.length,
    by_status: tally((t) => t.status || '—'),
    by_priority: tally((t) => t.priority || '—'),
    by_category: Object.entries(cat)
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count),
    recent: [...rows].sort((a, b) => b.created_at - a.created_at).slice(0, 12),
  }
}

/** A2.08 — xavfsizlik-dashboard: audit + four-eyes + xodim holatini bitta
 *  ko'rinishда jamlaydi (mavjud endpointlar ustidan, yangi backend kutmaydi). */
export interface SecurityOverview {
  four_eyes_open: number
  staff_total: number
  staff_active: number
  audit_actions: { key: string; count: number }[]
  recent_audit: AuditRow[]
}
export async function fetchSecurityOverview(signal?: AbortSignal): Promise<SecurityOverview> {
  const opt = signal ? { signal } : {}
  const [audit, fourEyes, staff] = await Promise.all([
    api.get<{ items: AuditRow[] }>('/admin/audit', { query: { limit: 300 }, ...opt }),
    api.get<{ items: FourEyesRow[] }>('/admin/four-eyes', { query: { limit: 200 }, ...opt }),
    api.get<{ staff: AdminStaff[] }>('/admin/staff', opt),
  ])
  const auditRows = audit.items ?? []
  const feRows = fourEyes.items ?? []
  const staffRows = staff.staff ?? []
  const actions: Record<string, number> = {}
  for (const a of auditRows) actions[a.action] = (actions[a.action] ?? 0) + 1
  return {
    four_eyes_open: feRows.filter((r) => r.status === 'pending' || r.status === 'approved').length,
    staff_total: staffRows.length,
    staff_active: staffRows.filter((s) => s.is_active).length,
    audit_actions: Object.entries(actions)
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10),
    recent_audit: auditRows.slice(0, 12),
  }
}

/** A2.09 — «Mening vazifalarim»: joriy xodimга tayinlangan ochiq tiketlar +
 *  uning tasdig'ini kutayotgan four-eyes so'rovlari. `/admin/me` identity ustidan
 *  jonli filtrlanadi — yangi backend kutmaydi. */
export interface MyTasks {
  me: AdminMe | null
  my_tickets: AdminTicket[]
  approvals: FourEyesRow[]
}
export async function fetchMyTasks(signal?: AbortSignal): Promise<MyTasks> {
  const opt = signal ? { signal } : {}
  const me = await fetchMe(signal)
  const [tickets, fourEyes] = await Promise.all([
    api.get<{ tickets: AdminTicket[] }>('/admin/tickets', { query: { limit: 300 }, ...opt }),
    api.get<{ items: FourEyesRow[] }>('/admin/four-eyes', { query: { limit: 200 }, ...opt }),
  ])
  const closed = (s: string) => s === 'closed' || s === 'resolved'
  const my_tickets = (tickets.tickets ?? []).filter(
    (t) => me != null && t.assigned_to === me.id && !closed(t.status),
  )
  // Boshqa xodim yaratgan, hali tasdiqlanmagan four-eyes — men tasdiqlay olaman.
  const approvals = (fourEyes.items ?? []).filter(
    (r) => r.status === 'pending' && (!me || r.requester !== me.username),
  )
  return { me, my_tickets, approvals }
}

/** A2.05 — infra-dashboard: Go-process runtime + DB sog'ligi (GET /admin/infra,
 *  read-only, backend `09c9fc6`). PII yo'q — pg_stat_activity + pool snapshot. */
export interface InfraHealth {
  runtime: {
    goroutines: number
    heap_alloc: number
    heap_sys: number
    num_cpu: number
    go_version: string
    gc_count: number
    uptime_seconds: number
  }
  db_pool: { acquired: number; idle: number; total: number; max: number }
  db: {
    name: string
    size_bytes: number
    active_conns: number
    pg_version: string
    migration: number
  }
}
export async function fetchInfraHealth(signal?: AbortSignal): Promise<InfraHealth> {
  const data = await api.get<{ infra: InfraHealth }>('/admin/infra', signal ? { signal } : {})
  return data.infra
}

/** A3.14 — foydalanuvchi egalik qilgan botlar (GET /admin/users/:id/bots,
 *  backend `2c204cf`, read-only bot_platform.bots). */
export interface AdminOwnedBot {
  id: number
  user_id: number
  username: string
  name: string
  created_at: number | null
  revoked_at: number | null
  revoked: boolean
}
export async function fetchUserBots(id: number, params: FetchParams): Promise<FetchResult<AdminOwnedBot>> {
  const data = await api.get<{ bots: AdminOwnedBot[] }>(`/admin/users/${id}/bots`, {
    signal: params.signal,
  })
  const rows = (data.bots ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/** A3.20 — bitta foydalanuvchi support-tiketlari (GET /admin/users/:id/tickets,
 *  backend `bbb3fa7`). AdminTicket tipini qayta ishlatadi. */
export async function fetchUserTickets(id: number, params: FetchParams): Promise<FetchResult<AdminTicket>> {
  const data = await api.get<{ tickets: AdminTicket[] }>(`/admin/users/${id}/tickets`, {
    signal: params.signal,
  })
  const rows = (data.tickets ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/** A3.17 — foydalanuvchidan VA unga qarshi shikoyatlar (GET /admin/users/:id/reports,
 *  backend `bbb3fa7`). AdminReport tipini qayta ishlatadi. */
export async function fetchUserReports(id: number, params: FetchParams): Promise<FetchResult<AdminReport>> {
  const data = await api.get<{ reports: AdminReport[] }>(`/admin/users/${id}/reports`, {
    signal: params.signal,
  })
  const rows = (data.reports ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/** A4.21 — guruh/kanalga qarshi shikoyatlar (GET /admin/groups/:id/reports).
 *  AdminReport tipini qayta ishlatadi; endpoint (lane-A backend patch) deploy
 *  bo'lgach jonli, shu paytgacha halol empty-state. */
export async function fetchGroupReports(id: number, params: FetchParams): Promise<FetchResult<AdminReport>> {
  try {
    const data = await api.get<{ reports: AdminReport[] }>(`/admin/groups/${id}/reports`, {
      signal: params.signal,
    })
    const rows = (data.reports ?? []).filter((x) => matchesFilters(x, params))
    return paginate(sortLocally(rows, params), params)
  } catch (e) {
    // Endpoint (lane-A backend patch) hali deploy bo'lmagan bo'lsa 404 → halol
    // empty-state (xato-holat EMAS). Boshqa xatolar odatdagidek ko'tariladi.
    if (e instanceof ApiError && e.status === 404) return paginate([], params)
    throw e
  }
}

/** A3.21 — Bog'liq akkauntlar (GET /admin/users/:id/related). Bir QURILMANI
 *  (device_uid) baham ko'radigan boshqa userlar — multi-akkaunt signali. IP bilan
 *  bog'lanmaydi (proksi). Endpoint (lane-A patch) deploy bo'lgach jonli; 404→empty. */
export interface AdminRelatedAccount {
  user_id: number
  username?: string | null
  first_name?: string | null
  last_name?: string | null
  device_uid: string
  platform: string
  device_model?: string | null
  last_active_at: number
}
export async function fetchRelatedAccounts(
  id: number,
  params: FetchParams,
): Promise<FetchResult<AdminRelatedAccount>> {
  try {
    const data = await api.get<{ related: AdminRelatedAccount[] }>(`/admin/users/${id}/related`, {
      signal: params.signal,
    })
    const rows = (data.related ?? []).filter((x) => matchesFilters(x, params))
    return paginate(sortLocally(rows, params), params)
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return paginate([], params)
    throw e
  }
}

/** A2.02 — real-time monitor: arzon rolling-hisoblagichlar (GET /admin/realtime,
 *  read-only, backend `b434043`). Panel har ~5s poll qiladi. */
export interface RealtimeCounters {
  active_5m: number
  messages_min: number
  messages_hour: number
  signups_min: number
  signups_hour: number
  series: { minute: string; messages: number }[]
}
export async function fetchRealtimeCounters(signal?: AbortSignal): Promise<RealtimeCounters> {
  const data = await api.get<{ realtime: RealtimeCounters }>('/admin/realtime', signal ? { signal } : {})
  return data.realtime
}

/** A6.01/15 — moderatsiya shikoyati (GET /admin/reports). Backend `4f05814`. */
export interface AdminReport {
  id: number
  reporter_id: number
  reporter_username?: string | null
  target_type: string
  target_id: number
  target_dialog_id?: number | null
  category: string
  reason?: string | null
  status: string
  resolution?: string | null
  resolved_by?: number | null
  resolved_at?: number | null
  created_at: number
}
export async function fetchReports(params: FetchParams): Promise<FetchResult<AdminReport>> {
  const data = await api.get<{ reports: AdminReport[] }>('/admin/reports', {
    query: { limit: 300 },
    signal: params.signal,
  })
  const rows = (data.reports ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
/** A6.02 — prioritetli navbat: yuqori-xavf kategoriyalar (csam/zo'ravonlik/firibgarlik/
 *  doxxing) + status=pending (client-side triage). Yangi backend YO'Q. */
export const HIGH_RISK_CATEGORIES = new Set(['csam', 'violence', 'fraud', 'doxxing'])
export async function fetchPriorityReports(params: FetchParams): Promise<FetchResult<AdminReport>> {
  // A6.02: server-side risk-ranked queue (category severity + flood volume + age)
  // via GET /admin/moderation/priority — returns only pending high-risk reports,
  // already ordered. sortLocally preserves that order unless the user sorts a
  // column; matchesFilters applies the search box. (Was: client-side triage over
  // /admin/reports capped at 300 — replaced by the scalable server endpoint.)
  const data = await api.get<{ items: AdminReport[] }>('/admin/moderation/priority', {
    query: { limit: 300 },
    signal: params.signal,
  })
  const rows = (data.items ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
/** A8.20 — adolat hisoboti uchun ban/unban audit yozuvlari (xom, /admin/audit). */
export async function fetchBanUnbanAudit(signal?: AbortSignal): Promise<AuditRow[]> {
  const data = await api.get<{ items: AuditRow[] }>('/admin/audit', {
    query: { limit: 500 },
    ...(signal ? { signal } : {}),
  })
  return (data.items ?? []).filter((a) => a.action === 'ban_user' || a.action === 'unban_user')
}
/** A7.03 — guruhlash: bir obyektga ko'p shikoyat. JONLI /admin/reports ni klientда
 *  (target_type,target_id) bo'yicha agregatlaydi — eng ko'p shikoyat qilingan obyektlar. */
export interface AdminReportGroup {
  target_type: string
  target_id: number
  total: number
  pending: number
  categories: string
  latest: number
}
export async function fetchReportGroups(params: FetchParams): Promise<FetchResult<AdminReportGroup>> {
  // A7.03 — server-side SQL aggregate (JONLI /admin/report-groups). Butun reports
  // jadvali bo'yicha guruhlanadi; klient endi faqat kichik agregat-natijani filtrlaydi.
  const data = await api.get<{ groups: AdminReportGroup[] }>('/admin/report-groups', {
    signal: params.signal,
  })
  const rows = (data.groups ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/** A6.03–08 — kategoriya bo'yicha izolyatsiyalangan navbat (server ?category= filtri). */
export async function fetchReportsByCategory(
  category: string,
  params: FetchParams,
): Promise<FetchResult<AdminReport>> {
  const data = await api.get<{ reports: AdminReport[] }>('/admin/reports', {
    query: { category, limit: 300 },
    signal: params.signal,
  })
  const rows = (data.reports ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
/** A7.04 — shikoyatchi ishonchliligi. JONLI /admin/reports ni shikoyatchi bo'yicha
 *  agregatlaydi — dismiss-rate yuqori = ko'p asossiz shikoyat (ishonchsiz). */
export interface AdminReporterRel {
  reporter_id: number
  username?: string | null
  total: number
  resolved: number
  dismissed: number
  dismiss_rate: number
}
export async function fetchReporterReliability(params: FetchParams): Promise<FetchResult<AdminReporterRel>> {
  // A7.04 — server-side SQL aggregate (JONLI /admin/report-reporters). dismiss_rate
  // serverда hisoblanadi; butun jadval bo'yicha, 300-cap yo'q.
  const data = await api.get<{ reporters: AdminReporterRel[] }>('/admin/report-reporters', {
    signal: params.signal,
  })
  const rows = (data.reporters ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
export async function fetchReport(id: number, signal?: AbortSignal): Promise<AdminReport | null> {
  const data = await api.get<{ report: AdminReport }>(`/admin/reports/${id}`, signal ? { signal } : {})
  return data.report ?? null
}
/** A6.19 — shikoyatni yopish (resolve) yoki rad etish (dismiss). Serverда audit. */
export function resolveReport(id: number, resolution: string, dismiss: boolean): Promise<unknown> {
  return api.post(`/admin/reports/${id}/resolve`, { resolution, dismiss })
}

/** A23.01 — xodim (GET /admin/staff). Parol-hash HECH QACHON qaytmaydi. */
export interface AdminStaff {
  id: number
  username: string
  role: string
  is_active: boolean
  permission_count: number
  created_at: number
  last_login_at?: number | null
}
export async function fetchStaff(params: FetchParams): Promise<FetchResult<AdminStaff>> {
  const data = await api.get<{ staff: AdminStaff[] }>('/admin/staff', { signal: params.signal })
  const rows = (data.staff ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
/** A23.02 — bitta xodim kartasi. Alohida endpoint yo'q: /admin/staff dan id bo'yicha. */
export async function fetchStaffMember(id: number, signal?: AbortSignal): Promise<AdminStaff | null> {
  const data = await api.get<{ staff: AdminStaff[] }>('/admin/staff', signal ? { signal } : {})
  return (data.staff ?? []).find((s) => s.id === id) ?? null
}

/** A23.03/04 — server rol katalogi (GET /admin/roles). Klient ko'prigining o'rnini bosadi. */
export interface AdminRoleDef {
  role: string
  permissions: string[]
  permission_count: number
  all: boolean
  assignable: boolean
}
export async function fetchRoleCatalog(signal?: AbortSignal): Promise<AdminRoleDef[]> {
  const data = await api.get<{ roles: AdminRoleDef[] }>('/admin/roles', signal ? { signal } : {})
  return data.roles ?? []
}
/** A23 — xodim rolini o'zgartirish (superadmin-gated serverда). */
export function setStaffRole(id: number, role: string): Promise<unknown> {
  return api.post(`/admin/staff/${id}/role`, { role })
}

// Create a brand-new staff/admin login (username + password + role). Superadmin
// only (server-gated). role must be an assignable role from fetchRoleCatalog.
export function createStaff(username: string, password: string, role: string): Promise<unknown> {
  return api.post('/admin/staff', { username, password, role })
}

/** A16.05/06/11 — analitika taqsimoti (GET /admin/analytics/breakdown). */
export interface AdminBreakdown {
  by_platform: { platform: string; users: number }[]
  by_chat_type: { type: string; count: number }[]
  active_hours: number[]
}
export async function fetchBreakdown(signal?: AbortSignal): Promise<AdminBreakdown> {
  const data = await api.get<AdminBreakdown>('/admin/analytics/breakdown', signal ? { signal } : {})
  return {
    by_platform: data.by_platform ?? [],
    by_chat_type: data.by_chat_type ?? [],
    active_hours: data.active_hours ?? [],
  }
}

/** A4.16 — guruh a'zo o'sish qatori (GET /admin/groups/:id/growth). */
export async function fetchGroupGrowth(
  id: number,
  signal?: AbortSignal,
): Promise<{ day: string; joined: number }[]> {
  const data = await api.get<{ growth: { day: string; joined: number }[] }>(
    `/admin/groups/${id}/growth`,
    signal ? { signal } : {},
  )
  return data.growth ?? []
}

/** A4.17 — guruh/kanal statistikasi (GET /admin/groups/:id/stats). PII-siz agregat. */
export interface AdminGroupStats {
  member_count: number
  admin_count: number
  new_members: { d7: number; d30: number }
  messages_7d: number
  messages_total: number
  messages_per_day: { day: string; messages: number }[]
  top_posters: { id: number; first_name: string; username?: string | null; messages: number }[]
}
export async function fetchGroupStats(id: number, signal?: AbortSignal): Promise<AdminGroupStats | null> {
  const data = await api.get<{ stats: AdminGroupStats }>(`/admin/groups/${id}/stats`, signal ? { signal } : {})
  return data.stats ?? null
}

/** A4.19 — boost holati (GET /admin/groups/:id/boosts). MyGram boostlari BEPUL (pul yo'q). */
export interface AdminBooster {
  user_id: number
  username?: string | null
  first_name?: string | null
  created_at: number
}
export interface AdminGroupBoosts {
  count: number
  level: number
  boosters: AdminBooster[]
}
export async function fetchGroupBoosts(id: number, signal?: AbortSignal): Promise<AdminGroupBoosts | null> {
  const data = await api.get<{ boosts: AdminGroupBoosts }>(`/admin/groups/${id}/boosts`, signal ? { signal } : {})
  return data.boosts ?? null
}
/** Boosterlar ro'yxatini jadval uchun sahifalaydi (xuddi shu endpoint). */
export async function fetchGroupBoosters(id: number, params: FetchParams): Promise<FetchResult<AdminBooster>> {
  const data = await api.get<{ boosts: AdminGroupBoosts }>(`/admin/groups/${id}/boosts`, {
    signal: params.signal,
  })
  const rows = (data.boosts?.boosters ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/** A4.14 — guruh/kanal media galereyasi (GET /admin/groups/:id/media). Baytlar
 *  proksilanmaydi (media-proxy user-token kutadi) — metadata + blurhash. */
export interface AdminMedia {
  file_id: string
  kind: string
  mime: string
  size_bytes: number
  width?: number | null
  height?: number | null
  duration_ms?: number | null
  blurhash?: string | null
  owner_id?: number | null
  username?: string | null
  seq: number
  created_at: number
}
export async function fetchGroupMedia(id: number, params: FetchParams): Promise<FetchResult<AdminMedia>> {
  const data = await api.get<{ media: AdminMedia[] }>(`/admin/groups/${id}/media`, {
    signal: params.signal,
  })
  const rows = (data.media ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/** A4.07 — guruh/kanal xabar tarixi (GET /admin/groups/:id/messages). */
export interface AdminMessage {
  seq: number
  sender_id: number
  username?: string | null
  type?: string | null
  text?: string | null
  view_count?: number | null
  edited: boolean
  deleted: boolean
  topic_id?: number | null
  created_at: number
}
export async function fetchGroupMessages(id: number, params: FetchParams): Promise<FetchResult<AdminMessage>> {
  const data = await api.get<{ messages: AdminMessage[] }>(`/admin/groups/${id}/messages`, {
    signal: params.signal,
  })
  const rows = (data.messages ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/** A4.11 — guruh admin harakatlar jurnali (GET /admin/groups/:id/admin-log). */
export interface AdminGroupLogEntry {
  action: string
  actor_id: number
  actor_username?: string | null
  target_id?: number | null
  target_username?: string | null
  meta?: Record<string, unknown> | null
  created_at: number
}
export async function fetchGroupAdminLog(id: number, params: FetchParams): Promise<FetchResult<AdminGroupLogEntry>> {
  const data = await api.get<{ items: AdminGroupLogEntry[] }>(`/admin/groups/${id}/admin-log`, {
    signal: params.signal,
  })
  const rows = (data.items ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/** A4.09 — qo'shilish so'rovi (GET /admin/groups/:id/join-requests). */
export interface AdminJoinRequest {
  user_id: number
  username?: string | null
  first_name?: string | null
  last_name?: string | null
  bio?: string | null
  created_at: number
}
export async function fetchGroupJoinRequests(
  id: number,
  params: FetchParams,
): Promise<FetchResult<AdminJoinRequest>> {
  const data = await api.get<{ requests: AdminJoinRequest[] }>(`/admin/groups/${id}/join-requests`, {
    signal: params.signal,
  })
  const rows = (data.requests ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/** A4.10 — forum topigi (GET /admin/groups/:id/topics). */
export interface AdminTopic {
  id: number
  topic_no: number
  title?: string | null
  icon_emoji?: string | null
  closed: boolean
  pinned: boolean
  is_general: boolean
  created_at: number
}
export async function fetchGroupTopics(id: number, params: FetchParams): Promise<FetchResult<AdminTopic>> {
  const data = await api.get<{ topics: AdminTopic[] }>(`/admin/groups/${id}/topics`, {
    signal: params.signal,
  })
  const rows = (data.topics ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/** A4.05 — guruh/kanal a'zosi (GET /admin/groups/:id/members). */
export interface AdminMember {
  user_id: number
  username?: string | null
  first_name?: string | null
  last_name?: string | null
  role: string
  custom_title?: string | null
  joined_at?: number | null
  restricted: boolean
  is_bot?: boolean
}
export async function fetchGroupMembers(id: number, params: FetchParams): Promise<FetchResult<AdminMember>> {
  const data = await api.get<{ members: AdminMember[] }>(`/admin/groups/${id}/members`, {
    signal: params.signal,
  })
  const rows = (data.members ?? []).filter((m) => matchesFilters(m, params))
  return paginate(sortLocally(rows, params), params)
}

/** A4.06 — adminlar: xuddi shu endpoint, faqat owner/admin (yangi backend YO'Q). */
export async function fetchGroupAdmins(id: number, params: FetchParams): Promise<FetchResult<AdminMember>> {
  // A4.06 — dedicated server-side role filter (JONLI /admin/groups/:id/admins).
  // 200-a'zolik sahifa cheklovi yo'q — katta guruhda ham HAMMA admin qaytadi.
  const data = await api.get<{ members: AdminMember[] }>(`/admin/groups/${id}/admins`, {
    signal: params.signal,
  })
  const rows = (data.members ?? []).filter((m) => matchesFilters(m, params))
  return paginate(sortLocally(rows, params), params)
}

/** A4.12 — ulangan botlar: dedicated server-side is_bot filtr (JONLI). */
export async function fetchGroupBots(id: number, params: FetchParams): Promise<FetchResult<AdminMember>> {
  const data = await api.get<{ members: AdminMember[] }>(`/admin/groups/${id}/bots`, {
    signal: params.signal,
  })
  const rows = (data.members ?? []).filter((m) => matchesFilters(m, params))
  return paginate(sortLocally(rows, params), params)
}

/** A3.11 — foydalanuvchi a'zo bo'lgan guruh/kanal (GET /admin/users/:id/groups). */
export interface AdminUserGroup {
  id: number
  type: string
  title?: string | null
  username?: string | null
  role: string
  status: string
  member_count: number
  joined_at?: number | null
}
export async function fetchUserGroups(id: number, params: FetchParams): Promise<FetchResult<AdminUserGroup>> {
  const data = await api.get<{ groups: AdminUserGroup[] }>(`/admin/users/${id}/groups`, {
    signal: params.signal,
  })
  const rows = (data.groups ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/** A3.13 — egalik qilgan guruh/kanallar: xuddi shu endpoint, faqat role='owner'. */
export async function fetchUserOwnedGroups(
  id: number,
  params: FetchParams,
): Promise<FetchResult<AdminUserGroup>> {
  const data = await api.get<{ groups: AdminUserGroup[] }>(`/admin/users/${id}/groups`, {
    signal: params.signal,
  })
  const rows = (data.groups ?? []).filter((x) => x.role === 'owner').filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/** A3.12 / A10.04 — foydalanuvchi hamyoni (GET /admin/users/:id/wallet). */
export interface AdminWalletTxn {
  id: number
  delta: number
  kind?: string | null
  peer_id?: number | null
  note?: string | null
  created_at: number
}
export interface AdminUserWallet {
  user_id: number
  balance: number
  updated_at?: number | null
  txns: AdminWalletTxn[]
}
export async function fetchUserWallet(id: number, signal?: AbortSignal): Promise<AdminUserWallet | null> {
  const data = await api.get<{ wallet: AdminUserWallet }>(`/admin/users/${id}/wallet`, signal ? { signal } : {})
  return data.wallet ?? null
}
/** Xuddi shu endpoint, faqat tranzaksiya ro'yxatini jadval uchun sahifalaydi. */
export async function fetchUserWalletTxns(
  id: number,
  params: FetchParams,
): Promise<FetchResult<AdminWalletTxn>> {
  const data = await api.get<{ wallet: AdminUserWallet }>(`/admin/users/${id}/wallet`, {
    signal: params.signal,
  })
  const rows = (data.wallet?.txns ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/**
 * A3.16 — Sovg'a / Stars tranzaksiyalari. Xuddi wallet endpointi, faqat sovg'a
 * turlari (`live_gift` = yuborilgan, `live_gift_received` = olingan). MyGram'da
 * Stars ekvivalenti = sovg'a-iqtisodi; A3.15 (barcha to'lovlar) dan farqli o'laroq
 * bu faqat sovg'a oqimini ko'rsatadi. Yangi backend YO'Q.
 */
export const GIFT_TXN_KINDS = new Set(['live_gift', 'live_gift_received'])
export interface AdminGiftSummary {
  sent: number
  received: number
  count: number
}
export async function fetchUserGiftTxns(
  id: number,
  params: FetchParams,
): Promise<FetchResult<AdminWalletTxn>> {
  const data = await api.get<{ wallet: AdminUserWallet }>(`/admin/users/${id}/wallet`, {
    signal: params.signal,
  })
  const rows = (data.wallet?.txns ?? [])
    .filter((x) => x.kind != null && GIFT_TXN_KINDS.has(x.kind))
    .filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
export function summarizeGifts(txns: AdminWalletTxn[]): AdminGiftSummary {
  let sent = 0
  let received = 0
  let count = 0
  for (const x of txns) {
    if (x.kind == null || !GIFT_TXN_KINDS.has(x.kind)) continue
    count++
    if (x.delta < 0) sent += -x.delta
    else received += x.delta
  }
  return { sent, received, count }
}

/** A10.03 — hamyonlar reytingi (GET /admin/wallets). */
export interface AdminWalletRow {
  user_id: number
  username?: string | null
  first_name?: string | null
  balance: number
  updated_at: number
}
export async function fetchWallets(params: FetchParams): Promise<FetchResult<AdminWalletRow>> {
  const data = await api.get<{ wallets: AdminWalletRow[] }>(`/admin/wallets`, { signal: params.signal })
  const rows = (data.wallets ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
/** A2.04 dashboard uchun xom massivlar (sahifalanmagan). */
export async function fetchWalletsRaw(signal?: AbortSignal): Promise<AdminWalletRow[]> {
  const data = await api.get<{ wallets: AdminWalletRow[] }>(`/admin/wallets`, signal ? { signal } : {})
  return data.wallets ?? []
}
export async function fetchTransactionsRaw(signal?: AbortSignal): Promise<AdminTransaction[]> {
  const data = await api.get<{ transactions: AdminTransaction[] }>(`/admin/transactions`, signal ? { signal } : {})
  return data.transactions ?? []
}

/** A4.04 — guruh/kanal kartasi (GET /admin/groups/:id). */
export interface AdminGroupDetail {
  id: number
  type: string
  title?: string | null
  username?: string | null
  about?: string | null
  member_count: number
  is_forum: boolean
  // A4.07 settings-snapshot maydonlari — admin `GetGroup` SELECT'ga ustunlar
  // qo'shilgach (lane-A backend patch) to'ladi; shu paytgacha undefined → «—».
  sign_messages?: boolean
  slow_mode_seconds?: number
  join_by_request?: boolean
  content_protected?: boolean
  history_visible?: boolean
  topic_layout?: string | null
  owner_id?: number | null
  owner_username?: string | null
  verified?: boolean // A4.29 admin verifikatsiya belgisi
  admin_blocked?: boolean // A4.27 admin blok (muzlatilgan)
  admin_blocked_reason?: string | null
  created_at: number
}
export async function fetchGroup(id: number, signal?: AbortSignal): Promise<AdminGroupDetail | null> {
  const data = await api.get<{ group: AdminGroupDetail }>(`/admin/groups/${id}`, signal ? { signal } : {})
  return data.group ?? null
}

/** A3.07 — bitta foydalanuvchi to'liq kartasi (GET /admin/users/:id). */
export interface AdminUserDetail {
  id: number
  username?: string | null
  first_name?: string | null
  last_name?: string | null
  phone?: string | null
  bio?: string | null
  country_code?: string | null
  email?: string | null
  status: string
  is_banned: boolean
  is_bot: boolean
  is_official: boolean
  banned_reason?: string | null
  user_number?: string | null
  birthday?: string | null
  /** epoch ms */
  created_at: number
  last_seen?: number | null
}
export async function fetchUser(id: number, signal?: AbortSignal): Promise<AdminUserDetail | null> {
  const data = await api.get<{ user: AdminUserDetail }>(`/admin/users/${id}`, signal ? { signal } : {})
  return data.user ?? null
}

/** A3.10 — foydalanuvchi qurilmasi/sessiyasi (token-hash YO'Q). */
export interface AdminSession {
  id: number
  platform?: string | null
  device_model?: string | null
  os_version?: string | null
  app_version?: string | null
  last_ip?: string | null
  last_active?: number | null
  created_at: number
  revoked_at?: number | null
  active: boolean
  two_factor_pending: boolean
}
export async function fetchUserSessions(id: number, params: FetchParams): Promise<FetchResult<AdminSession>> {
  const data = await api.get<{ sessions: AdminSession[] }>(`/admin/users/${id}/sessions`, {
    signal: params.signal,
  })
  const rows = (data.sessions ?? []).filter((s) => matchesFilters(s, params))
  return paginate(sortLocally(rows, params), params)
}

/** A19 — huquqiy so'rov (GET /admin/legal). Admin-only. Backend `697c6be`. */
export interface AdminLegalRequest {
  id: number
  kind: string
  authority: string
  reference?: string | null
  jurisdiction?: string | null
  affected_user_id?: number | null
  username?: string | null
  status: string
  details?: string | null
  resolution?: string | null
  created_at: number
  resolved_at?: number | null
}
export async function fetchLegalRequests(params: FetchParams): Promise<FetchResult<AdminLegalRequest>> {
  const data = await api.get<{ requests: AdminLegalRequest[] }>('/admin/legal', {
    query: { limit: 300 },
    signal: params.signal,
  })
  const rows = (data.requests ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
export async function fetchLegalRequest(id: number, signal?: AbortSignal): Promise<AdminLegalRequest | null> {
  const data = await api.get<{ request: AdminLegalRequest }>(`/admin/legal/${id}`, signal ? { signal } : {})
  return data.request ?? null
}
export function createLegalRequest(body: {
  kind: string
  authority: string
  reference?: string
  jurisdiction?: string
  affected_user_id?: number
  details?: string
}): Promise<{ id: number }> {
  return api.post<{ id: number }>('/admin/legal', body)
}
/** A19.12 — rad etilgan huquqiy so'rovlar (status=rejected). Client-side. */
export async function fetchRejectedLegal(params: FetchParams): Promise<FetchResult<AdminLegalRequest>> {
  const data = await api.get<{ requests: AdminLegalRequest[] }>('/admin/legal', {
    query: { limit: 300 },
    signal: params.signal,
  })
  const rows = (data.requests ?? []).filter((x) => x.status === 'rejected').filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
/** A19.13 — shaffoflik hisoboti uchun xom ro'yxat (barcha statuslar, ≤300). */
export async function fetchAllLegal(signal?: AbortSignal): Promise<AdminLegalRequest[]> {
  const data = await api.get<{ requests: AdminLegalRequest[] }>('/admin/legal', {
    query: { limit: 300 },
    ...(signal ? { signal } : {}),
  })
  return data.requests ?? []
}
export function setLegalStatus(id: number, status: string, resolution: string): Promise<unknown> {
  return api.post(`/admin/legal/${id}/status`, { status, resolution })
}

/** A9 — verifikatsiya arizasi (GET /admin/verification). Backend `663ef50`. */
export interface AdminVerification {
  id: number
  user_id: number
  username?: string | null
  kind: string
  evidence?: string | null
  status: string
  decision_note?: string | null
  created_at: number
  reviewed_at?: number | null
}
export async function fetchVerifications(params: FetchParams): Promise<FetchResult<AdminVerification>> {
  const data = await api.get<{ requests: AdminVerification[] }>('/admin/verification', {
    query: { limit: 300 },
    signal: params.signal,
  })
  const rows = (data.requests ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
/** A9.13 — soxta/rad etilgan verifikatsiya arizalari (status=rejected). Client-side. */
export async function fetchRejectedVerifications(params: FetchParams): Promise<FetchResult<AdminVerification>> {
  const data = await api.get<{ requests: AdminVerification[] }>('/admin/verification', {
    query: { limit: 300 },
    signal: params.signal,
  })
  const rows = (data.requests ?? []).filter((x) => x.status === 'rejected').filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
/** A9.14 — statistika uchun xom ro'yxat (barcha statuslar, ≤300). */
export async function fetchAllVerifications(signal?: AbortSignal): Promise<AdminVerification[]> {
  const data = await api.get<{ requests: AdminVerification[] }>('/admin/verification', {
    query: { limit: 300 },
    ...(signal ? { signal } : {}),
  })
  return data.requests ?? []
}
export async function fetchVerification(id: number, signal?: AbortSignal): Promise<AdminVerification | null> {
  const data = await api.get<{ request: AdminVerification }>(`/admin/verification/${id}`, signal ? { signal } : {})
  return data.request ?? null
}
export function decideVerification(id: number, approve: boolean, note: string): Promise<unknown> {
  return api.post(`/admin/verification/${id}/decide`, { approve, note })
}
/** A9.12 — verifikatsiyalanganlar reyestri (GET /admin/verified). */
export interface AdminVerifiedUser {
  id: number
  username?: string | null
  first_name?: string | null
  last_name?: string | null
  is_bot: boolean
  created_at: number
}
export async function fetchVerified(params: FetchParams): Promise<FetchResult<AdminVerifiedUser>> {
  const data = await api.get<{ verified: AdminVerifiedUser[] }>('/admin/verified', {
    query: { limit: 300 },
    signal: params.signal,
  })
  const rows = (data.verified ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/** A8.10-12 — apellyatsiya (GET /admin/appeals). Backend `548626f`. */
export interface AdminAppeal {
  id: number
  user_id: number
  username?: string | null
  kind: string
  strike_id?: number | null
  reason: string
  status: string
  decision_note?: string | null
  created_at: number
  reviewed_at?: number | null
}
export async function fetchAppeals(params: FetchParams): Promise<FetchResult<AdminAppeal>> {
  const data = await api.get<{ appeals: AdminAppeal[] }>('/admin/appeals', {
    query: { limit: 300 },
    signal: params.signal,
  })
  const rows = (data.appeals ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
export async function fetchAppeal(id: number, signal?: AbortSignal): Promise<AdminAppeal | null> {
  const data = await api.get<{ appeal: AdminAppeal }>(`/admin/appeals/${id}`, signal ? { signal } : {})
  return data.appeal ?? null
}
/** A8.12 — qaror: grant (ijroni bekor qiladi) yoki reject. */
export function decideAppeal(id: number, grant: boolean, note: string): Promise<unknown> {
  return api.post(`/admin/appeals/${id}/decide`, { grant, note })
}

/** A8.03/A3.18 — progressiv-jazo strayk (GET /admin/strikes). Backend `0252c93`. */
export interface AdminStrike {
  id: number
  user_id?: number
  username?: string | null
  severity: number
  reason: string
  report_id?: number | null
  issued_by?: string | null
  active: boolean
  expires_at?: number | null
  created_at: number
}
export interface AdminUserStrikes {
  strikes: AdminStrike[]
  active_points: number
}
export async function fetchStrikes(params: FetchParams): Promise<FetchResult<AdminStrike>> {
  const data = await api.get<{ strikes: AdminStrike[] }>('/admin/strikes', {
    query: { limit: 300 },
    signal: params.signal,
  })
  const rows = (data.strikes ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
export async function fetchUserStrikes(id: number, signal?: AbortSignal): Promise<AdminUserStrikes> {
  const data = await api.get<AdminUserStrikes>(`/admin/users/${id}/strikes`, signal ? { signal } : {})
  return { strikes: data.strikes ?? [], active_points: data.active_points ?? 0 }
}
/** A8.19 — cheklov statistikasi uchun xom ro'yxat (≤300). */
export async function fetchAllStrikes(signal?: AbortSignal): Promise<AdminStrike[]> {
  const data = await api.get<{ strikes: AdminStrike[] }>('/admin/strikes', {
    query: { limit: 300 },
    ...(signal ? { signal } : {}),
  })
  return data.strikes ?? []
}
export function issueStrike(
  id: number,
  severity: number,
  reason: string,
  reportId?: number,
  expiresDays?: number,
): Promise<unknown> {
  return api.post(`/admin/users/${id}/strike`, {
    severity,
    reason,
    ...(reportId ? { report_id: reportId } : {}),
    ...(expiresDays ? { expires_days: expiresDays } : {}),
  })
}

/** A8.01 — aktiv cheklov (bloklangan user, GET /admin/banned). Backend `e876f10`. */
export interface AdminBannedUser {
  id: number
  username?: string | null
  first_name?: string | null
  last_name?: string | null
  banned_reason?: string | null
  banned_at: number
}
export async function fetchBanned(params: FetchParams): Promise<FetchResult<AdminBannedUser>> {
  const data = await api.get<{ banned: AdminBannedUser[] }>('/admin/banned', {
    query: { limit: 300 },
    signal: params.signal,
  })
  const rows = (data.banned ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/** X2 daraja 2: bloklashда sabab MAJBURIY (server ham talab qiladi + auditga yozadi). */
export function banUser(id: number, reason: string): Promise<unknown> {
  return api.post(`/admin/users/${id}/ban`, { reason })
}
export function unbanUser(id: number): Promise<unknown> {
  return api.post(`/admin/users/${id}/unban`, {})
}
/** A3.31 — majburiy chiqarish (barcha sessiyalar). POST /admin/users/:id/force-logout.
 *  Endpoint (lane-A patch) deploy bo'lgach ishlaydi. */
export function forceLogout(id: number, reason: string): Promise<unknown> {
  return api.post(`/admin/users/${id}/force-logout`, { reason })
}

/* ── A11.02 Davlatlar ─────────────────────────────────────────── */

/** A11.03 — bitta davlat kartasi. Alohida endpoint yo'q: ro'yxatdan kod bo'yicha topiladi. */
export async function fetchCountry(code: string, signal?: AbortSignal): Promise<CountryRow | null> {
  const data = await api.get<{ countries: CountryRow[] }>('/admin/countries', signal ? { signal } : {})
  const want = code.toUpperCase()
  return (data.countries ?? []).find((c) => c.code.toUpperCase() === want) ?? null
}

/** A11.20 — davlat bo'yicha o'sish: ro'yxatni ro'yxatdan-o'tganlar soni bo'yicha kamayuvchi tartibda. */
export async function fetchCountryGrowth(signal?: AbortSignal): Promise<CountryRow[]> {
  const data = await api.get<{ countries: CountryRow[] }>('/admin/countries', signal ? { signal } : {})
  return (data.countries ?? []).filter((c) => c.users > 0).sort((a, b) => b.users - a.users)
}

/** A11.11 — raqam prefikslari: davlatlarni dial-kod bo'yicha guruhlaydi (+7 = RU/KZ
 *  kabi ulashilgan prefikslar ko'rinadi). Alohida endpoint yo'q. */
export interface DialPrefixRow {
  dial_code: string
  countries: { code: string; name: string; flag?: string | null }[]
  users: number
  shared: boolean
}
export async function fetchDialPrefixes(params: FetchParams): Promise<FetchResult<DialPrefixRow>> {
  const data = await api.get<{ countries: CountryRow[] }>('/admin/countries', { signal: params.signal })
  const acc = new Map<string, DialPrefixRow>()
  for (const c of data.countries ?? []) {
    const key = c.dial_code || '?'
    const entry = { code: c.code, name: c.name, flag: c.flag ?? null }
    const cur = acc.get(key)
    if (!cur) acc.set(key, { dial_code: key, countries: [entry], users: c.users, shared: false })
    else {
      cur.countries.push(entry)
      cur.users += c.users
    }
  }
  const rows = [...acc.values()]
    .map((r) => ({ ...r, shared: r.countries.length > 1 }))
    .filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

export async function fetchCountries(params: FetchParams): Promise<FetchResult<CountryRow>> {
  // Diqqat: javob `{countries:[…]}` — tekis massiv EMAS (jonli tekshirilgan).
  const data = await api.get<{ countries: CountryRow[] }>('/admin/countries', {
    signal: params.signal,
  })
  const needle = params.search.trim().toLowerCase()

  const filtered = (data.countries ?? [])
    .filter((c) =>
      !needle ||
      c.name.toLowerCase().includes(needle) ||
      c.code.toLowerCase().includes(needle) ||
      c.dial_code.includes(needle),
    )
    .filter((c) => matchesFilters(c, params))

  return paginate(sortLocally(filtered, params), params)
}

/* ── A18.01 Audit jurnali ─────────────────────────────────────── */

/** A8.02 — ban/unban tarixi: audit'ni foydalanuvchi ban/unban amaliga filtrlaydi
 *  (yangi endpoint EMAS — mavjud /admin/audit ustidan). Sabab `detail` ustunida. */
export async function fetchBanAuditLog(params: FetchParams): Promise<FetchResult<AuditRow>> {
  // A8.02 — server-side action filter (JONLI /admin/audit/search). Ban va unban
  // alohida so'ralib birlashtiriladi — 500-cap fetch-all-then-filter yo'q.
  const [ban, unban] = await Promise.all([
    api.get<{ items: AuditRow[] }>('/admin/audit/search', { query: { action: 'ban_user', limit: 500 }, signal: params.signal }),
    api.get<{ items: AuditRow[] }>('/admin/audit/search', { query: { action: 'unban_user', limit: 500 }, signal: params.signal }),
  ])
  const needle = params.search.trim().toLowerCase()
  const rows = [...(ban.items ?? []), ...(unban.items ?? [])]
    .filter(
      (a) =>
        !needle ||
        (a.target ?? '').toLowerCase().includes(needle) ||
        (a.admin ?? '').toLowerCase().includes(needle) ||
        (a.detail ?? '').toLowerCase().includes(needle),
    )
    .filter((a) => matchesFilters(a, params))
  return paginate(sortLocally(rows, params), params)
}

/** A11.22 — davlat o'zgarishlar jurnali: audit'ni faqat davlat blok/ochish amaliga
 *  filtrlaydi (yangi endpoint EMAS — mavjud /admin/audit ustidan). */
export async function fetchCountryAuditLog(params: FetchParams): Promise<FetchResult<AuditRow>> {
  // A11.22 — server-side action filter (JONLI /admin/audit/search). block/unblock
  // alohida so'ralib birlashtiriladi.
  const [blocked, unblocked] = await Promise.all([
    api.get<{ items: AuditRow[] }>('/admin/audit/search', { query: { action: 'block_country', limit: 500 }, signal: params.signal }),
    api.get<{ items: AuditRow[] }>('/admin/audit/search', { query: { action: 'unblock_country', limit: 500 }, signal: params.signal }),
  ])
  const needle = params.search.trim().toLowerCase()
  const rows = [...(blocked.items ?? []), ...(unblocked.items ?? [])]
    .filter(
      (a) =>
        !needle ||
        (a.target ?? '').toLowerCase().includes(needle) ||
        (a.admin ?? '').toLowerCase().includes(needle),
    )
    .filter((a) => matchesFilters(a, params))
  return paginate(sortLocally(rows, params), params)
}

/** A18.04 — xodim faoliyati: audit oynasini (server cap 200) xodim bo'yicha agregatlaydi.
 *  Alohida endpoint yo'q — mavjud /admin/audit ustidan klient-agregat. */
export interface StaffActivityRow {
  admin: string
  actions: number
  last_action: string
  last_at: number
  last_ip?: string | null
}
export async function fetchStaffActivity(params: FetchParams): Promise<FetchResult<StaffActivityRow>> {
  // A18.04 — server-side per-admin GROUP BY (JONLI /admin/staff-activity). Butun
  // audit jurnali bo'yicha; 200-oyna klient-agregat cheklovi yo'q.
  const data = await api.get<{ items: StaffActivityRow[] }>('/admin/staff-activity', {
    signal: params.signal,
  })
  const rows = (data.items ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

export async function fetchAudit(params: FetchParams): Promise<FetchResult<AuditRow>> {
  const data = await api.get<{ items: AuditRow[] }>('/admin/audit', {
    query: { limit: Math.min(500, params.pageSize * 5) },
    signal: params.signal,
  })
  const needle = params.search.trim().toLowerCase()
  const filtered = (data.items ?? [])
    .filter(
      (a) =>
        !needle ||
        a.action.toLowerCase().includes(needle) ||
        (a.admin ?? '').toLowerCase().includes(needle) ||
        (a.target ?? '').toLowerCase().includes(needle),
    )
    .filter((a) => matchesFilters(a, params))

  return paginate(sortLocally(filtered, params), params)
}

/** A18.02 — server-side filtrli audit qidiruv (butun jurnal, faqat yaqin oyna emas). */
export interface AuditSearchParams {
  action?: string
  admin?: string
  target?: string
  since?: number
  until?: number
  limit?: number
}
export async function searchAudit(p: AuditSearchParams, signal?: AbortSignal): Promise<AuditRow[]> {
  const query: Record<string, string | number> = {}
  if (p.action) query.action = p.action
  if (p.admin) query.admin = p.admin
  if (p.target) query.target = p.target
  if (p.since) query.since = p.since
  if (p.until) query.until = p.until
  query.limit = p.limit ?? 200
  const d = await api.get<{ items: AuditRow[] }>('/admin/audit/search', { query, ...(signal ? { signal } : {}) })
  return d.items ?? []
}
export async function fetchAuditActions(signal?: AbortSignal): Promise<string[]> {
  const d = await api.get<{ actions: string[] }>('/admin/audit/actions', signal ? { signal } : {})
  return d.actions ?? []
}

/** A18.05 — shubhali xodim faoliyati (destruktiv-amal outlier, N kunlik oyna). */
export interface SuspiciousStaffRow {
  admin: string
  total: number
  destructive: number
  targets: number
  last_at: string | number
  suspicious: boolean
  reason: string // '' | 'high_absolute' | 'statistical_outlier'
}
export async function fetchSuspiciousStaff(days = 7, signal?: AbortSignal): Promise<SuspiciousStaffRow[]> {
  const d = await api.get<{ items: SuspiciousStaffRow[] }>('/admin/audit/staff-anomalies', {
    query: { days, limit: 100 },
    ...(signal ? { signal } : {}),
  })
  return d.items ?? []
}

/* ── A18.06 Four-eyes so'rovlari ──────────────────────────────────
   Migratsiya `000076_four_eyes_requests` bazada yaratiladigan jadval.
   Quyidagi turlar — SHU jadvalning ustunlari, ya'ni server javobi uchun
   KUTILAYOTGAN kontrakt. A yo'li endpointni shu shaklda yozsa, adapter
   o'zgartirilmaydi.

   ✅ ENDPOINT JONLI (2026-07-20, `ab354dc`): 6 metod `/admin/four-eyes*`
   deploy qilindi va `curl` bilan 8/8 guardrail tasdiqlandi (self-approve→403,
   duplicate→409, server danger_level/required_role belgilaydi). Shakl bu
   yerdagi `FourEyesRow` bilan bayt-mos. */

/** Endpoint JONLI → navbat haqiqiy ma'lumot ko'rsatadi. */
export const FOUR_EYES_LIVE = true

export type FourEyesStatus =
  | 'pending' | 'approved' | 'rejected' | 'executed' | 'expired' | 'cancelled'

export interface FourEyesRow {
  id: number
  requester: string
  action: string
  target: string
  reason: string
  /** 4 yoki 5 (migratsiya: ck_fe_level). */
  danger_level: 4 | 5
  required_role: string
  status: FourEyesStatus
  approver?: string | null
  approver_note?: string | null
  /** Ta'sir hisoboti so'rov paytida MUZLATILADI (danger-actions.md). */
  impact?: Record<string, unknown> | null
  /** Epoch ms — jonli yadro sanani RAQAM qaytaradi. */
  created_at: number
  expires_at: number
  approved_at?: number | null
  /** 5-daraja: tasdiqdan keyingi 10 daqiqalik kutish davri tugashi. */
  execute_after?: number | null
  executed_at?: number | null
}

export async function fetchFourEyes(params: FetchParams): Promise<FetchResult<FourEyesRow>> {
  const data = await api.get<{ items: FourEyesRow[]; total?: number }>('/admin/four-eyes', {
    query: { limit: params.pageSize, offset: (params.page - 1) * params.pageSize },
    signal: params.signal,
  })
  const rows = (data.items ?? []).filter((r) => matchesFilters(r, params))
  return typeof data.total === 'number'
    ? { rows, total: data.total }
    : paginate(sortLocally(rows, params), params)
}

/* Four-eyes amallari (JONLI, `ab354dc`). Server qoidalari (self-approve,
   executor=requester, required_role) shu chaqiruvlarda emas — SERVERDA
   majburlanadi; klient shunchaki chaqiradi va xatoni ko'rsatadi. */

/** So'rov yaratish. `danger_level`/`required_role` KIRMAYDI — server amaldan aniqlaydi. */
export function createFourEyesRequest(body: {
  action: string
  target: string
  reason: string
  impact?: Record<string, unknown>
  payload?: Record<string, unknown>
}): Promise<{ id: number }> {
  return api.post<{ id: number }>('/admin/four-eyes', body)
}

export function approveFourEyes(id: number, approverNote?: string): Promise<unknown> {
  return api.post(`/admin/four-eyes/${id}/approve`, { approver_note: approverNote ?? '' })
}
export function rejectFourEyes(id: number, approverNote?: string): Promise<unknown> {
  return api.post(`/admin/four-eyes/${id}/reject`, { approver_note: approverNote ?? '' })
}
export function cancelFourEyes(id: number, reason: string): Promise<unknown> {
  return api.post(`/admin/four-eyes/${id}/cancel`, { reason })
}
export function executeFourEyes(id: number): Promise<unknown> {
  return api.post(`/admin/four-eyes/${id}/execute`, {})
}

/* ── Amallar ──────────────────────────────────────────────────────
   BUZUVCHI AMALLAR ATAYLAB YO'Q (`banUser`, `blockCountry` yozilmagan).

   Sabab: jonli endpointlar SABABNI qabul qilmaydi (`ban` handleri body
   o'qimaydi, `admin_audit.detail` bo'sh yoziladi), X2 esa 2-darajadan
   boshlab sababni majburiy qiladi. Yordamchi funksiya shu yerda tursa —
   kimdir uni «tayyor» deb ulab qo'yadi va nazorat dekorativ bo'lib qoladi.

   Qo'shiladi: `POST /admin/users/:id/ban` `{reason}` ni qabul qilib,
   uni `admin_audit.detail` ga yozgandan keyin (tz/admin/HOLAT.md §2). */

export async function fetchStats(signal?: AbortSignal): Promise<AdminStats> {
  // Diqqat: javob `{stats:{…}}` — bir qavat ichkarida (jonli tekshirilgan).
  const data = await api.get<{ stats: AdminStats }>('/admin/stats', signal ? { signal } : {})
  return data.stats
}

/* ── RBAC KONTRAKTI — A dan `GET /admin/me` (tz/admin/HOLAT.md §2-P1) ──
   A YO'LI UCHUN SHAKL: server shu javobni qaytarsin, keyin klient
   `lib/permissions.ts` dagi VAQTINCHALIK rol→huquq ko'prigini o'chiradi va
   `permissions[]` ni serverdan oladi (huquq = `{modul}.{resurs}.{amal}`;
   `'*'` = super_admin). `roles[]` = TZ §1.2 16-rol modeli; `role` = eski
   yagona `admins.role` (orqaga-moslik). Endpoint hali YO'Q — `fetchMe` 404 da
   `null` qaytaradi, klient ko'prikка qaytadi (buzilmaydi). */
export interface AdminMe {
  id: number
  username: string
  role: string
  roles: string[]
  permissions: string[]
}
export async function fetchMe(signal?: AbortSignal): Promise<AdminMe | null> {
  try {
    const data = await api.get<AdminMe>('/admin/me', signal ? { signal } : {})
    return data ?? null
  } catch {
    // Endpoint hali qurilmagan (404) → klient rol→huquq ko'prigi ishlaydi.
    return null
  }
}

/* ── A2.01 Analitika (§219) ───────────────────────────────────────
   ⚠️ SHAKL `stats` DAN BOSHQACHA: bu yerda o'rov YO'Q — maydonlar
   to'g'ridan-to'g'ri `data` ostida. Va `messages_per_day[].day` — ISO
   SATR (`2026-07-19`), epoch-ms EMAS. Ikkalasi ham `curl` bilan
   tekshirilgan (HOLAT.md §5: shaklni taxmin qilma). */

export interface AdminAnalytics {
  dau: number
  mau: number
  messages_7d: number
  messages_per_day: { day: string; messages: number }[]
  registrations: { today: number; d7: number; d30: number }
  funnel: { total_users: number; sent_first_message: number; activation_rate: number }
}

export async function fetchAnalytics(signal?: AbortSignal): Promise<AdminAnalytics> {
  return api.get<AdminAnalytics>('/admin/analytics', signal ? { signal } : {})
}

/**
 * `registrations` va `funnel.total_users` bir jadvalni HAR XIL filtr bilan
 * sanaydi (`admin.go`: :139–141 da `deleted_at IS NULL` YO'Q, :166 da BOR).
 * Natijada «7 kunda ro'yxatdan o'tgan» jamidan KO'P chiqishi mumkin — bu
 * imkonsiz ko'rinadi va panelga ishonchni yo'qotadi.
 *
 * Server tuzatilmaguncha buni YASHIRMAYMIZ — UI ogohlantirish ko'rsatadi.
 * Batafsil: tz/admin/HOLAT.md §2-P2.
 */
export function registrationsInconsistent(a: AdminAnalytics | null): boolean {
  if (!a) return false
  return a.registrations.d7 > a.funnel.total_users
}

/* ── A21 Bozor: sotuvchi arizalari + kategoriya (s10 iqtisod) ──────────────── */

export interface AdminSellerRow {
  user_id: number
  name: string
  username?: string
  shop_name: string
  status: string
  region?: string
  activity?: string
  reject_reason?: string
  rating: number
  deals_done: number
  verified: boolean
  created_at: string
}

/** GET /admin/market/sellers — sotuvchi arizalari (pending birinchi). Filtr/saralash klientda. */
export async function fetchSellerApplications(params: FetchParams): Promise<FetchResult<AdminSellerRow>> {
  const data = await api.get<{ sellers: AdminSellerRow[] }>('/admin/market/sellers', { signal: params.signal })
  const rows = (data.sellers ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

export function decideSeller(userId: number, approve: boolean, reason: string) {
  return api.post(`/admin/market/sellers/${userId}/decide`, { approve, reason })
}
export function verifySeller(userId: number, verified: boolean) {
  return api.post(`/admin/market/sellers/${userId}/verify`, { verified })
}

export interface AdminCategoryRow {
  id: number
  parent_id?: number | null
  title: string
  icon?: string
  position: number
  enabled: boolean
  listings: number
}

/** GET /admin/market/categories — barcha kategoriya (o'chirilgani ham). */
export async function fetchMarketCategories(params: FetchParams): Promise<FetchResult<AdminCategoryRow>> {
  const data = await api.get<{ categories: AdminCategoryRow[] }>('/admin/market/categories', { signal: params.signal })
  const rows = (data.categories ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

export function createCategory(body: { title: string; icon: string; parent_id: number; position: number }) {
  return api.post<{ id: number }>('/admin/market/categories', body)
}
export function updateCategory(id: number, body: { title: string; icon: string; position: number; enabled: boolean }) {
  return api.put(`/admin/market/categories/${id}`, body)
}
export function deleteCategory(id: number) {
  return api.del(`/admin/market/categories/${id}`)
}

/* ── A21 Ball (points) admin: earn-rules + katalog (s10 iqtisod) ───────────── */

export interface AdminEarnRuleRow {
  event: string
  amount: number
  daily_cap?: number | null
  enabled: boolean
}
export async function fetchEarnRules(params: FetchParams): Promise<FetchResult<AdminEarnRuleRow>> {
  const data = await api.get<{ rules: AdminEarnRuleRow[] }>('/admin/points/earn-rules', { signal: params.signal })
  const rows = (data.rules ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
export function updateEarnRule(event: string, body: { amount: number; daily_cap: number | null; enabled: boolean }) {
  return api.put(`/admin/points/earn-rules/${encodeURIComponent(event)}`, body)
}

export interface AdminPointsCatalogRow {
  code: string
  title: string
  price: number
  category: string
  enabled: boolean
  purchases: number
}
export async function fetchPointsCatalog(params: FetchParams): Promise<FetchResult<AdminPointsCatalogRow>> {
  const data = await api.get<{ items: AdminPointsCatalogRow[] }>('/admin/points/catalog', { signal: params.signal })
  const rows = (data.items ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
export function upsertPointsCatalog(body: { code: string; title: string; price: number; category: string; enabled: boolean }) {
  return api.post('/admin/points/catalog', body)
}
export function deletePointsCatalog(code: string) {
  return api.del(`/admin/points/catalog/${encodeURIComponent(code)}`)
}

/* ── A24 Tizim sozlamalari (real, consumed config — no fake toggles) ─────────── */

export interface AdminSystemSettings {
  /** Enforced by middleware.Maintenance — 503 on user routes when true. */
  maintenance_mode: boolean
  /** Consumed by the live-stream service (streamer revenue %). */
  live_gift_streamer_pct: number
  /** Consumed by the live-stream service (max co-hosts). */
  live_max_cohosts: number
}

export async function fetchSystemSettings(signal?: AbortSignal): Promise<AdminSystemSettings> {
  return api.get<AdminSystemSettings>('/admin/settings/system', { signal })
}

export function updateSystemSettings(body: Partial<AdminSystemSettings>) {
  return api.put('/admin/settings/system', body)
}

/* ── Ball gumondorlar (anti-farm review, 28 §5) ───────────────────────────── */
export interface AdminSuspicionRow {
  id: number
  user_id: number
  user_name: string
  reason: string
  score: number
  status: string
  created_at: string
}
export async function fetchSuspicions(params: FetchParams): Promise<FetchResult<AdminSuspicionRow>> {
  const data = await api.get<{ suspicions: AdminSuspicionRow[] }>('/admin/points/suspicions', { signal: params.signal })
  const rows = (data.suspicions ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
export function resolveSuspicion(id: number, sanction: boolean) {
  return api.post(`/admin/points/suspicions/${id}/resolve`, { sanction })
}

/* ── Stikerlar moderatsiyasi (10 §A21) ────────────────────────────────────── */
export interface AdminStickerSetRow {
  id: number
  owner_id: number
  owner_name: string
  title: string
  short_name: string
  thumb_file_id: string
  status: string
  sticker_count: number
  installs: number
  animated: boolean
  reject_reason?: string
}
/** GET /admin/stickers/sets?status= — pending birinchi. Filtr/saralash klientda. */
export async function fetchStickerSets(params: FetchParams): Promise<FetchResult<AdminStickerSetRow>> {
  const data = await api.get<{ sets: AdminStickerSetRow[] }>('/admin/stickers/sets', { signal: params.signal })
  const rows = (data.sets ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}
export function moderateStickerSet(id: number, approve: boolean, reason: string) {
  return api.post(`/admin/stickers/sets/${id}/moderate`, { approve, reason })
}

/* ── A28.02 Admin 2FA (TOTP) ─────────────────────────────────── */
export interface AdminSecurity {
  two_factor_enabled: boolean
}
export function fetchAdminSecurity(signal?: AbortSignal): Promise<AdminSecurity> {
  return api.get<AdminSecurity>('/admin/account/security', { signal })
}
export function enroll2FA(): Promise<{ secret: string; otpauth_uri: string }> {
  return api.post('/admin/account/2fa/enroll')
}
export function verify2FA(code: string): Promise<{ two_factor_enabled: boolean }> {
  return api.post('/admin/account/2fa/verify', { code })
}
export function disable2FA(code: string): Promise<{ two_factor_enabled: boolean }> {
  return api.post('/admin/account/2fa/disable', { code })
}

/* ── A22 Localization (server-managed translation store) ──────── */
export interface LocaleLang {
  lang: string
  count: number
  coverage_pct?: number
  missing?: number
}
export interface LocaleString {
  key: string
  value: string
  updated_at?: string | number
}
export async function fetchLocaleLangs(signal?: AbortSignal): Promise<LocaleLang[]> {
  const d = await api.get<{ langs: LocaleLang[] }>('/admin/localization/langs', { signal })
  return d.langs ?? []
}
export async function fetchLocaleStrings(lang: string, q: string, signal?: AbortSignal): Promise<LocaleString[]> {
  const d = await api.get<{ strings: LocaleString[] }>('/admin/localization/strings', { query: { lang, q }, signal })
  return d.strings ?? []
}
export function setLocaleString(lang: string, key: string, value: string): Promise<unknown> {
  return api.put('/admin/localization/strings', { lang, key, value })
}
export function deleteLocaleString(lang: string, key: string): Promise<unknown> {
  return api.del('/admin/localization/strings', { query: { lang, key } })
}
export function fetchLocaleStats(signal?: AbortSignal): Promise<{ reference_keys: number; langs: LocaleLang[] }> {
  return api.get('/admin/localization/stats', { signal })
}
